/**
 * Server-driven inspection capability hook.
 * Source of truth is `shop_inspection_capabilities` table, NOT frontend feature flags.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';

export interface InspectionCapabilities {
  /** Core: can the shop use workflow inspections at all */
  canInspect: boolean;
  /** Core: AI auto-placement of evidence to checklist items */
  canAutoPlace: boolean;
  /** Catalyst+: AI auto-grading (G/Y/R condition suggestion) */
  canAutoGrade: boolean;
  /** Catalyst+: AI auto-notes (drafted technician notes) */
  canAutoNotes: boolean;
  /** Permission: can manage inspection templates (MANAGER/ORG_ADMIN) */
  canManageTemplates: boolean;
  /** Permission: can perform/edit inspections (TECHNICIAN/MANAGER/ORG_ADMIN) */
  canEditInspection: boolean;
  /** Loading state */
  isLoading: boolean;
}

export function useInspectionCapabilities(shopId?: string): InspectionCapabilities {
  const { activeShop } = useAuth();
  const { hasRole } = usePermissions();
  const resolvedShopId = shopId || activeShop?.id;

  const { data, isLoading } = useQuery({
    queryKey: ['inspection-capabilities', resolvedShopId],
    queryFn: async () => {
      if (!resolvedShopId) return null;
      const { data, error } = await supabase
        .from('shop_inspection_capabilities' as any)
        .select('can_inspect, can_auto_place, can_auto_grade, can_auto_notes')
        .eq('shop_id', resolvedShopId)
        .maybeSingle();
      if (error) {
        console.error('[useInspectionCapabilities] Error:', error.message);
        return null;
      }
      return data as unknown as { can_inspect: boolean; can_auto_place: boolean; can_auto_grade: boolean; can_auto_notes: boolean } | null;
    },
    enabled: !!resolvedShopId,
    staleTime: 5 * 60 * 1000, // 5 min cache -- capabilities rarely change
  });

  return {
    canInspect: data?.can_inspect ?? true,
    canAutoPlace: data?.can_auto_place ?? true,
    canAutoGrade: data?.can_auto_grade ?? false,
    canAutoNotes: data?.can_auto_notes ?? false,
    canManageTemplates: hasRole('MANAGER', 'ORG_ADMIN'),
    canEditInspection: hasRole('TECHNICIAN', 'MANAGER', 'ORG_ADMIN'),
    isLoading,
  };
}
